import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "@reducers/rootReducer";

const deepClone = (obj: any) => JSON.parse(JSON.stringify(obj));

const paramsSerializer = (params: any) => {
  const clonedParams = params ? deepClone(params) : {};
  const searchParams = new URLSearchParams();

  if (clonedParams?.page) {
    for (const [key, value] of Object.entries(clonedParams.page)) {
      searchParams.set(`page[${key}]`, String(value));
    }
    delete clonedParams.page;
  }
  if (clonedParams?.include && Array.isArray(clonedParams.include)) {
    searchParams.set("include", clonedParams.include.join(","));
    delete clonedParams.include;
  }
  if (clonedParams?.sort && Array.isArray(clonedParams.sort)) {
    searchParams.set("sort", clonedParams.sort.join(","));
    delete clonedParams.sort;
  }

  if (clonedParams) {
    for (const [key, value] of Object.entries(clonedParams)) {
      if (value && typeof value === "object") {
        for (const [subKey, subValue] of Object.entries(value)) {
          searchParams.set(`${key}[${subKey}]`, subValue as string);
        }
      } else {
        searchParams.set(key, value as string);
      }
    }
  }

  return searchParams.toString();
};

const rawBaseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_SCOPESTACK_API_HOST as string,
  prepareHeaders: (headers: Headers, { getState, endpoint }) => {
    const token = (getState() as RootState).auth.token;
    // Multipart uploads send a FormData body. Setting Content-Type here would
    // override the browser's auto-generated `multipart/form-data; boundary=...`,
    // leaving the boundary off so Rack parses zero params (param is missing).
    // Let the browser set the header for upload endpoints.
    if (!/upload/i.test(endpoint)) {
      headers.set("Content-Type", "application/vnd.api+json");
    }
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
    return headers;
  },
  paramsSerializer,
  // Not every v2 endpoint answers with JSON:API - the service/document CSV
  // exports reply `text/csv` via `send_data`. The default "json" handler runs
  // `JSON.parse` over the raw body and turns a perfectly good 200 into a
  // PARSING_ERROR. "content-type" picks the handler off the response header
  // instead; RTK matches /ion\/(vnd\.api\+)?json/, so JSON:API still parses as
  // JSON and anything else falls through to text.
  responseHandler: "content-type",
});

// Same race as v1/me in ScopeStackAPI.ts: a /me fired right after sign-in can
// 404 before the user is resolvable, so recast it once after a short delay.
const baseQuery: typeof rawBaseQuery = async (args, api, extraOptions) => {
  const result = await rawBaseQuery(args, api, extraOptions);
  const url = typeof args === "string" ? args : args.url;
  if (result.error?.status === 404 && url.endsWith("/v2/me")) {
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return rawBaseQuery(args, api, extraOptions);
  }
  return result;
};

export const scopeStackApiV2 = createApi({
  reducerPath: "scopeStackApiV2",
  baseQuery,
  endpoints: () => ({}),
});
